import { Button, ButtonSize } from '@lemon8/design';
import { Link, useNavigate } from '@remix-run/react';
import { FC, useCallback } from 'react';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useTranslation } from 'react-i18next';
import { UserAuthIcon } from '~/components/user-auth-icon';
import { Avatar } from '~/components/avatar';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { useSEOHref } from '~/utils/build-seo-url';
import { ArticleDetailUtil } from 'shared/models';
import BackIcon from 'icon/chevron_left.svg';
import { useNumFormatter } from '~/utils/format';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { PostDetailModalAtoms } from '../../atoms';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';

import './style.scss';

export const AuthorBar: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefix = useScopedAtomValue($CurrentPositionPrefix);
  const article = useScopedAtomValue(PostDetailModalAtoms.$CurrentArticle);
  const { author } = article;
  const activeToArticle = useActiveToArticle(article);

  const formatNum = useNumFormatter();

  const handleClose = (): void => {
    window.Tea('wap_click', {
      current_page_module: prefix + ModuleType.main_content,
      current_position: prefix + ClickPosition.modal_close_arrow,
    });
    navigate(-1);
  };

  const mainAuthorRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: prefix + ModuleType.main_content,
        current_position: prefix + ClickPosition.main_author,
      });
    }, []),
  });

  const followBtnObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: prefix + ModuleType.main_content,
        current_position: prefix + ClickPosition.main_author_follow_btn,
      });
    }, []),
  });

  const authorHref = useSEOHref(ArticleDetailUtil.getAuthorHref(article));

  const handleAuthorInfoClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToArticle([prefix, ClickPosition.main_author]);
      },
      clickEvent: () => ({
        current_position: ClickPosition.main_author,
        page_click_id: author.userId,
      }),
    },
    [activeToArticle, author],
  );

  const handleFollowClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToArticle([prefix, ClickPosition.main_author_follow_btn]);
      },
      defaultBehavior: () => {
        activeToArticle([prefix, ClickPosition.main_author_follow_btn]);
      },
      clickEvent: () => ({
        current_position: prefix + ClickPosition.main_author_follow_btn,
        page_click_id: author.userId,
      }),
    },
    [activeToArticle, author],
  );

  return (
    <div className="post-detail-modal-author-bar" ref={mainAuthorRef}>
      <BackIcon className="post-detail-modal-author-bar-close" onClick={handleClose} />
      <Link className="post-detail-modal-author-bar-avatar" to={authorHref} onClick={handleAuthorInfoClick}>
        <Avatar src={author.avatar} alt={author.nickName} />
      </Link>
      <Link className="post-detail-modal-author-bar-desc" to={authorHref} onClick={handleAuthorInfoClick}>
        <div className="name">
          <span>{author.nickName}</span>
          <UserAuthIcon authInfo={author.authInfo} width={16} height={16} />
        </div>
        {Boolean(author.followerCount) && (
          <p className="follower">
            {t('follower_count_plural', { num: author.followerCount }).replace('%d', formatNum(author.followerCount))}
          </p>
        )}
      </Link>
      <div className="post-detail-modal-author-bar-follow" ref={followBtnObservedRef}>
        <Button variant="Secondary" size={ButtonSize.small} title={t('follow')} onClick={handleFollowClick} />
      </div>
    </div>
  );
};
